import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../../../api";
import { useLogos } from "../../../context/LogoProvider";

const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");
const VIDEO_UPLOAD_ENDPOINT = `${API_BASE}/upload/lesson-video`;

const VideoUploadModal = ({ open, mode = "new", initialSrc = "", onClose, onUploaded }) => {
    const { getSystemConfig } = useLogos();
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [remotePath, setRemotePath] = useState(initialSrc || "");
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedFile(null);
            setError("");
            setPreviewUrl("");
            setRemotePath(initialSrc || "");
        }
    }, [open, initialSrc]);

    if (!open) return null;

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("video/")) {
            setError("Sólo se permiten archivos de video.");
            return;
        }

        // Validar tamaño máximo (Dinámico)
        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_VIDEOS', '1024'));
        const MAX_SIZE = maxMB * 1024 * 1024;

        if (file.size > MAX_SIZE) {
            setError(`El video es demasiado grande. El límite es de ${maxMB}MB.`);
            return;
        }

        setError("");
        setSelectedFile(file);
        const blobUrl = URL.createObjectURL(file);
        setPreviewUrl(blobUrl);

        try {
            setUploading(true);

            // 🗑️ BORRAR VIDEO ANTERIOR DEL S3 SI EXISTE
            if (initialSrc && initialSrc.includes('s3.amazonaws.com')) {
                try {
                    console.log('[VideoUploadModal] 🗑️ Eliminando video anterior de S3:', initialSrc);
                    await axios.delete(VIDEO_UPLOAD_ENDPOINT, {
                        params: { filePath: initialSrc },
                        withCredentials: true
                    });
                    console.log('[VideoUploadModal] ✅ Video anterior eliminado de S3');
                } catch (deleteError) {
                    console.warn('[VideoUploadModal] ⚠️ Error al eliminar video anterior:', deleteError);
                }
            }

            const formData = new FormData();
            formData.append("file", file);

            const resUpload = await axios.post(VIDEO_UPLOAD_ENDPOINT, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                withCredentials: true
            });

            const data = resUpload.data;
            if (!data.filePath) {
                throw new Error("La respuesta del servidor no incluye filePath.");
            }

            console.log('[VideoUploadModal] ✅ Nuevo video subido a S3:', data.filePath);
            setRemotePath(data.filePath);
        } catch (err) {
            console.error("Error al subir video:", err);
            setError(err.message || "Error al subir el video al servidor.");
            setSelectedFile(null);
            setPreviewUrl("");
            setRemotePath(initialSrc || "");
        } finally {
            setUploading(false);
        }
    };

    const handleConfirm = () => {
        if (!remotePath) {
            setError("Primero selecciona y sube un video.");
            return;
        }
        onUploaded?.(remotePath);
        onClose?.();
    };

    const videoUrl = previewUrl || (remotePath ? (remotePath.startsWith('http') ? remotePath : `${SERVER_URL}${remotePath}`) : "");

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: "min(640px, 96vw)",
                    maxHeight: "85vh",
                    background: "#fff",
                    borderRadius: 12,
                    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <div
                    className="roles-topbar"
                    style={{
                        marginBottom: "0.6rem",
                        paddingRight: 16,
                        paddingLeft: 16,
                        paddingTop: 8,
                        paddingBottom: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flex: "0 0 auto",
                    }}
                >
                    <h3 className="roles-title" style={{ marginRight: 0 }}>
                        {mode === "new" ? "Agregar video" : "Editar video"}
                    </h3>
                    <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
                        ✕
                    </button>
                </div>

                <div
                    style={{
                        padding: 16,
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        flex: "1 1 auto",
                        overflowY: "auto",
                    }}
                >
                    {/* Área de carga con preview integrado */}
                    <div
                        className={`file-uploader${remotePath ? ' file-uploader--filled' : ''}${error ? ' file-uploader--error' : ''}${uploading ? ' file-uploader--uploading' : ''}`}
                    >
                        {remotePath && (
                            <div className="file-uploader__badge">
                                <span>✓</span>
                                <span>Archivo cargado</span>
                            </div>
                        )}

                        {videoUrl && (
                            <div className="file-uploader__preview">
                                <video
                                    src={videoUrl}
                                    controls
                                    style={{ backgroundColor: '#000' }}
                                />
                            </div>
                        )}

                        <label className="file-uploader__label">
                            <input
                                type="file"
                                accept="video/*"
                                onChange={handleFileChange}
                                onClick={(e) => { e.target.value = ''; }}
                                style={{ display: 'none' }}
                                disabled={uploading}
                            />
                            <div className="file-uploader__icon">
                                {uploading ? '⏳' : remotePath ? '🔄' : '🎦'}
                            </div>
                            <div className="file-uploader__text">
                                {uploading ? 'Subiendo video...' : remotePath ? 'Cambiar video' : 'Haz clic para subir video'}
                            </div>
                            <div className="file-uploader__hint">
                                Formatos de video (tamaño según configuración del servidor)
                            </div>
                        </label>
                    </div>

                    {error && (
                        <small style={{ color: "#b91c1c", fontSize: ".8rem" }}>{error}</small>
                    )}
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: ".6rem",
                        marginTop: ".8rem",
                        paddingRight: 16,
                        paddingBottom: 12,
                        flex: "0 0 auto",
                    }}
                >
                    <button className="roles-btn" onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        className="btn-action"
                        onClick={handleConfirm}
                        disabled={uploading || !remotePath}
                        style={{
                            opacity: uploading || !remotePath ? 0.5 : 1,
                            cursor: uploading || !remotePath ? "not-allowed" : "pointer",
                        }}
                    >
                        {uploading ? "Subiendo..." : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VideoUploadModal;
